import { useState } from 'react'
import { Search, Filter, MoreVertical, Star, Trash2, Archive, Mail, Reply, Forward } from 'lucide-react'
import { Mail as MailType } from '../types'

const Inbox = () => {

  const [currentMail, setCurrentMail] = useState<MailType | null>(null)

  // Generate sample mail data
  const generateMails = (): MailType[] => {
    const mails: MailType[] = []
    const senders = [
      { name: 'John Doe', email: 'john@example.com', avatar: 'J' },
      { name: 'Jane Smith', email: 'jane@example.com', avatar: 'J' },
      { name: 'Mike Johnson', email: 'mike@example.com', avatar: 'M' },
      { name: 'Sarah Wilson', email: 'sarah@example.com', avatar: 'S' },
      { name: 'David Brown', email: 'david@example.com', avatar: 'D' }
    ]

    for (let i = 0; i < 20; i++) {
      const sender = senders[i % senders.length]
      const date = new Date()
      date.setDate(date.getDate() - i)
      
      mails.push({
        id: i + 1,
        unread: Math.random() > 0.5,
        from: {
          id: i + 1,
          name: sender.name,
          email: sender.email,
          avatar: { src: '', alt: sender.avatar },
          status: 'subscribed',
          location: 'New York'
        },
        subject: `Important message ${i + 1}`,
        body: `This is the body of email ${i + 1}. It contains some important information that you need to read.`,
        date: date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      })
    }
    
    return mails
  }

  const mails = generateMails()



  const toggleStar = (mailId: number) => {
    // In a real app, this would update the mail's starred status
    console.log('Toggle star for mail:', mailId)
  }

  const getStatusBadge = (unread: boolean) => {
    if (unread) {
      return (
        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
      )
    }
    return null
  }

  return (
    <div className="flex h-full">
      {/* Mail List */}
      <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search emails..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              <Filter className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto h-full">
          {mails.map((mail) => (
            <div
              key={mail.id}
              className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                currentMail?.id === mail.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
              }`}
              onClick={() => setCurrentMail(mail)}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {mail.from.avatar?.alt || mail.from.name.charAt(0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium truncate ${
                      mail.unread ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {mail.from.name}
                    </p>
                    <div className="flex items-center space-x-1">
                      {getStatusBadge(mail.unread || false)}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleStar(mail.id)
                        }}
                        className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className={`text-sm truncate ${
                    mail.unread ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {mail.subject}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {mail.body}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {mail.date}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mail Content */}
      <div className="flex-1 bg-white dark:bg-gray-800">
        {currentMail ? (
          <div className="h-full flex flex-col">
            {/* Mail Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {currentMail.from.avatar?.alt || currentMail.from.name.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                      {currentMail.subject}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      From: {currentMail.from.name} &lt;{currentMail.from.email}&gt;
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      {currentMail.date}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                    <Reply className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                    <Forward className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                    <Archive className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Mail Body */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="prose dark:prose-invert max-w-none">
                <p>{currentMail.body}</p>
                <p className="mt-4">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                </p>
                <p className="mt-4">
                  Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Mail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No mail selected</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Choose a mail from the list to read
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Inbox
